import { supabase } from '../lib/supabase';

export interface AgentFeedback {
  id: string;
  user_id: string;
  rating: number;
  comment: string | null;
  created_at: string;
}

export interface AgentFeedbackWithUser extends AgentFeedback {
  user_name: string | null;
  user_email: string | null;
}

export interface FeedbackStats {
  total_feedback: number;
  average_rating: number | null;
  rating_distribution: Record<string, number> | null;
  recent_count: number;
}

// Submit feedback from user
export async function submitAgentFeedback(
  userId: string,
  rating: number,
  comment?: string
): Promise<AgentFeedback> {
  const { data, error } = await supabase
    .from('agent_feedback')
    .insert({
      user_id: userId,
      rating,
      comment: comment?.trim() || null,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

// Get user's last feedback date
export async function getLastFeedbackDate(userId: string): Promise<Date | null> {
  const { data, error } = await supabase
    .from('agent_feedback')
    .select('created_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data ? new Date(data.created_at) : null;
}

// Check if we should show the feedback prompt (every 4 weeks)
export async function shouldShowFeedbackPrompt(userId: string): Promise<boolean> {
  const lastFeedback = await getLastFeedbackDate(userId);

  if (!lastFeedback) {
    // Never gave feedback - check if user has been active with AI for at least a week
    const { count, error } = await supabase
      .from('agent_conversations')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);

    if (error) throw error;

    // Show feedback prompt after at least 3 conversations
    return (count || 0) >= 3;
  }

  // Check if 4 weeks (28 days) have passed since last feedback
  const fourWeeksAgo = new Date();
  fourWeeksAgo.setDate(fourWeeksAgo.getDate() - 28);

  return lastFeedback < fourWeeksAgo;
}

// Admin: Get all feedback
export async function getAdminAgentFeedback(
  limit: number = 50,
  offset: number = 0
): Promise<AgentFeedbackWithUser[]> {
  const { data, error } = await supabase.rpc('get_admin_agent_feedback', {
    p_limit: limit,
    p_offset: offset,
  });

  if (error) throw error;
  return data || [];
}

// Admin: Get feedback stats
export async function getAdminFeedbackStats(): Promise<FeedbackStats> {
  const { data, error } = await supabase.rpc('get_admin_feedback_stats');

  if (error) throw error;
  return data as FeedbackStats;
}
